import { useQuery, useQueryClient, useMutation, UseMutationOptions, UseMutationResult, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { IPagePayload, PageType } from "@src/types/api/allPages";
import { TResponse } from "../../../types/common";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { detailPage, savePage } from "./callbacks";

// /* ---------------------- Save Page Mutation ---------------------- */
export const useAllPageSave = (
  pageType: PageType,
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<IPagePayload>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<TResponse<IPagePayload>, unknown, IPagePayload> = {}
): UseMutationResult<TResponse<IPagePayload>, unknown, IPagePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IPagePayload>, unknown, IPagePayload>({
    mutationFn: async (data: IPagePayload) => {
      const response = await savePage(pageType,data);
      return response.data; // ✅ return TResponse<IPagePayload>, not AxiosResponse
    },
    onSuccess: (res, ...rest) => {
      if (res.data?.pageType) {
        queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ALL_PAGE_DETAIL, res.data.pageType] });
      }
      onSuccess?.(res);
      options.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options.onError?.(error, ...rest);
    },
    ...options,
  });
};

// /* ---------------------- Get Page Detail Query ---------------------- */
export const useAllPageDetail = (
  pageType: PageType,
  options?: Omit<UseQueryOptions<TResponse<IPagePayload>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<IPagePayload>, Error> => {
  return useQuery<TResponse<IPagePayload>, Error>({
    queryKey: [API_ENDPOINTS.ALL_PAGE_DETAIL, pageType],
    queryFn: async () => {
      const res = await detailPage(pageType);
      return res.data; // ✅ return TResponse<IPagePayload>
    },
    enabled: !!pageType,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};
